/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Plan your Events implementation", description = "Decide what to track, how to identify users, how strictly to validate events, how to handle coverage gaps, and where events should go.", lastUpdatedDate = new Date(1787702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Plan your Events implementation",
        description: "Decide what to track, how to identify users, how strictly to validate events, how to handle coverage gaps, and where events should go.",
        lastUpdatedDate: "Wed Aug 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What you'll decide",
          "depth": 0,
          "id": "what-youll-decide"
        }, {
          "value": "Design your event model",
          "depth": 0,
          "id": "design-your-event-model"
        }, {
          "value": "Plan identity",
          "depth": 0,
          "id": "plan-identity"
        }, {
          "value": "Decide your contract strategy",
          "depth": 0,
          "id": "decide-your-contract-strategy"
        }, {
          "value": "Plan for coverage",
          "depth": 0,
          "id": "plan-for-coverage"
        }, {
          "value": "Choose how events reach destinations",
          "depth": 0,
          "id": "choose-how-events-reach-destinations"
        }, {
          "value": "Build your implementation",
          "depth": 0,
          "id": "build-your-implementation"
        }, {
          "value": "What's next",
          "depth": 0,
          "id": "whats-next"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams, analytics teams, engineers, and marketers who use event data"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["Familiarity with ", _jsx(_components.a, {
                href: "/events/overview",
                children: "how Events works"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Plan these decisions before you instrument Hightouch Events. They are harder to change after events start flowing because your warehouse tables, contracts, and destinations depend on them."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "what-youll-decide",
        children: "What you'll decide"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#design-your-event-model",
            children: "Event model"
          }), " — what to track and how to name and structure events"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#plan-identity",
            children: "Identity"
          }), " — how to recognize people and connect their activity over time"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#decide-your-contract-strategy",
            children: "Contract strategy"
          }), " — what to validate and how strictly"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#plan-for-coverage",
            children: "Coverage"
          }), " — how reliable client-side collection needs to be"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#choose-how-events-reach-destinations",
            children: "Delivery"
          }), " — where events go and how each destination should receive them"]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "design-your-event-model",
        children: "Design your event model"
      }), "\n", _jsx(_components.p, {
        children: "Decide which actions to track and how each event should be named and structured. These choices affect your warehouse tables, contracts, audiences, reports, and destinations."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Track what you'll use."
          }), " Tie each event to a real use case, such as an audience, journey, report, or destination workflow. Tracking everything \"just in case\" creates noise and tables no one uses."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name events consistently."
          }), " Pick one convention, such as ", _jsx(_components.code, {
            children: "Object Action"
          }), " (", _jsx(_components.code, {
            children: "Order Completed"
          }), "), and use it everywhere. Event names become warehouse table names, and contracts match them exactly. Inconsistent names can create duplicate tables or break validation."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Separate properties from traits."
          }), " Properties describe the action, such as cart total. Traits describe the person, such as email or plan type. Adding person attributes to every event can make tables harder to manage."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Decide required and optional properties."
          }), " Define which fields each event must include so contracts can validate them later."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Keep field types consistent."
          }), " A price should always be a number. A flag should always be a boolean. Hightouch infers each column's type from the values it receives, so mixed types can cause rejected or changed data."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Go deeper: ", _jsx(_components.a, {
          href: "/events/event-spec",
          children: "event types and payload structure"
        }), ", ", _jsx(_components.a, {
          href: "/events/warehouses/schema",
          children: "warehouse schema"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "plan-identity",
        children: "Plan identity"
      }), "\n", _jsx(_components.p, {
        children: "Decide how to recognize people before and after they are known, and how their activity should connect to one profile."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Choose a stable ", _jsx(_components.code, {
              children: "userId"
            }), "."]
          }), " Use an identifier that does not change over a person's lifetime, such as your internal database ID. Avoid email addresses, phone numbers, and session IDs because they can change or rotate. Send email and phone as traits instead."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connect anonymous and known activity."
          }), " A visitor starts with an ", _jsx(_components.code, {
            children: "anonymousId"
          }), ". Call ", _jsx(_components.code, {
            children: "identify"
          }), " when they sign in so their pre-login and post-login activity can be connected."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Decide how profiles unify."
          }), " Joining one person's anonymous and known activity can happen in a warehouse model. Unifying many identifiers across devices, sessions, and sources into one profile is the job of Identity Resolution. ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "Customer Studio"
          }), " can then use that identity layer for traits and audiences."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "If you're migrating, preserve identity behavior."
          }), " Recreate your current tool's identity priority logic with identifier rules so people resolve consistently while both systems run in parallel."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "decide-your-contract-strategy",
        children: "Decide your contract strategy"
      }), "\n", _jsxs(_components.p, {
        children: ["Decide which event rules to enforce and how strict those rules should be. A ", _jsx(_components.a, {
          href: "/events/contracts/management",
          children: "data contract"
        }), " validates the event name, field names, and field types exactly as written, including capitalization and whitespace."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Choose an enforcement level for each violation type."
          }), " Hightouch can allow the event and flag the violation, block the event, or filter out the invalid fields. The default is to allow."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Start permissive, then tighten."
          }), " Use a permissive setup first so you can see violations without dropping data. Tighten enforcement once event names and field types are stable."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Prioritize important events."
          }), " Add contracts for your core, high-value events before covering edge cases."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Go deeper: ", _jsx(_components.a, {
          href: "/events/contracts/management",
          children: "managing contracts"
        }), ", ", _jsx(_components.a, {
          href: "/events/contracts/violations",
          children: "handling violations"
        }), ", ", _jsx(_components.a, {
          href: "/events/contracts/cli",
          children: "Events CLI"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "plan-for-coverage",
        children: "Plan for coverage"
      }), "\n", _jsx(_components.p, {
        children: "Decide how complete your client-side event collection needs to be and which events need a more reliable path. Installing the SDK does not guarantee that every browser event will be captured."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Expect some client-side loss."
          }), " Browser events can be blocked by ad blockers, privacy settings such as Safari ITP, and cookie limits. This is part of client-side tracking, not necessarily a setup issue."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use first-party and server-side collection where needed."
          }), " Proxy SDK traffic through your own domain with ", _jsx(_components.a, {
            href: "/events/first-party-tracking",
            children: "first-party tracking"
          }), ". Send business-critical events, such as purchases and signups, through ", _jsx(_components.a, {
            href: "/events/sdks/http",
            children: "server-side collection"
          }), " so they do not depend on the browser."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Choose which events must be reliable."
          }), " Route those server-side from the start."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Go deeper: ", _jsx(_components.a, {
          href: "/events/first-party-tracking",
          children: "first-party tracking"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "choose-how-events-reach-destinations",
        children: "Choose how events reach destinations"
      }), "\n", _jsx(_components.p, {
        children: "Decide where each event should go and how each destination should receive it."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Warehouse, streaming, or both."
          }), " Write events to your warehouse for analytics, modeling, audience building, and long-term reuse. Stream events directly to a tool only when the workflow is time-sensitive, such as triggering a message as soon as an action happens. Many teams warehouse all events and stream only the events that need to be immediate."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event-shaped or profile-shaped data."
          }), " A destination can receive data as events, which represent actions over time, or as profile properties, which update attributes on a person. Choose based on what the destination should do with the data. Sending events when you meant to update profiles, or the reverse, is a common setup mistake."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Go deeper: ", _jsx(_components.a, {
          href: "/events/warehouses/warehouse",
          children: "write events to your warehouse"
        }), ", ", _jsx(_components.a, {
          href: "/events/event-streaming",
          children: "event streaming"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "build-your-implementation",
        children: "Build your implementation"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've made the decisions above, follow these steps to set up Events. If you're migrating from another tool, follow the ", _jsx(_components.a, {
          href: "/events/migration-guide/overview",
          children: "Migration guide"
        }), " instead — it adds the work of running both systems in parallel, proving your data matches, and cutting over without downtime."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connect your sources."
          }), " Instrument your app, site, or backend to send events to Hightouch using an SDK, the HTTP API, or a streaming source. See ", _jsx(_components.a, {
            href: "/events/sources/overview",
            children: "Collect events"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Send events to your warehouse."
          }), " Configure a warehouse destination so events land in a consistent schema you can query and model. See ", _jsx(_components.a, {
            href: "/events/warehouses/warehouse",
            children: "Send events to your warehouse"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Govern your events with contracts."
          }), " Define ", _jsx(_components.a, {
            href: "/events/contracts/management",
            children: "data contracts"
          }), " for your core events so bad data is caught before it reaches your warehouse. Start permissive and tighten as your event names and types stabilize."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Validate the data."
          }), " For a fresh build, confirm events arrive as expected: inspect live events in the ", _jsx(_components.a, {
            href: "/events/sources/overview#debugger",
            children: "debugger"
          }), " and the ", _jsx(_components.a, {
            href: "/events/sources/overview#event-catalog",
            children: "event catalog"
          }), ", and check that your warehouse tables and columns match what you sent (see ", _jsx(_components.a, {
            href: "/events/warehouses/schema",
            children: "Warehouse schema"
          }), "). If you're migrating, validate parity against your previous tool instead — see ", _jsx(_components.a, {
            href: "/events/migration-guide/validating-your-migration",
            children: "Validate your migration"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Set up monitoring."
          }), " Add volume, violation, and sync alerts so you catch issues early. See ", _jsx(_components.a, {
            href: "/events/monitoring",
            children: "Monitor events"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Depending on your use cases, you may also:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Stream events to destinations"
          }), " for time-sensitive workflows, alongside or instead of the warehouse. See ", _jsx(_components.a, {
            href: "/events/event-streaming",
            children: "Stream events to destinations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Transform events"
          }), " in flight to rename properties, filter, or enrich them. See ", _jsx(_components.a, {
            href: "/events/functions/overview",
            children: "Transform events with functions"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Improve coverage"
          }), " for browser events by ", _jsx(_components.a, {
            href: "/events/first-party-tracking",
            children: "proxying through your own domain"
          }), " and moving critical events to ", _jsx(_components.a, {
            href: "/events/sdks/http",
            children: "server-side collection"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "whats-next",
        children: "What's next"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Migrating"
          }), " from another tool? Follow the ", _jsx(_components.a, {
            href: "/events/migration-guide/overview",
            children: "Migration guide"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Once events are flowing, ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "build audiences in Customer Studio"
          }), " from your event data."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
